import type { ReactNode } from 'react'
import type { CmCase, CmCategory, CmSeverity, CmStatus, SignalFacet, SignalTag } from '@bloo-team/api-client'
import type { TriageSuggestion } from '../../lib/signal-ai'
import type { EntityHistory, MatchingRule, Prevalence } from '../../lib/signal-history'
import type { Disposition, PinnedRow, Signal, SignalEntity, SignalTriage } from '../../lib/signal-model'
import type { Campaign } from '../../lib/signal-attribution'
import type { PromotedSubgraph, StoryTab } from '../../lib/signal-subgraphs'
import type { SuppressionTerms } from '../../lib/suppression'

/** Inline 30d context for an entity chip, keyed by signal-model entityKey. */
export interface EntityChipContext {
  count30d: number
  first: number | null
  rare: boolean | null
}

/**
 * Prop contracts for the signal components. Components are
 * props-in/callbacks-out (components/search/index.ts rule): every store
 * write, route change and query job stays with the host page
 * (routes/.../vantage.signals.tsx).
 */

/** The docked signal pane's sections, in order (`?ptab` names the one to scroll to; Overview is the default and stays out of the URL). */
export const SIGNAL_TABS = ['overview', 'entities', 'events', 'kb', 'storyline', 'activity'] as const
export type SignalTab = (typeof SIGNAL_TABS)[number]
export const isSignalTab = (v: unknown): v is SignalTab => typeof v === 'string' && (SIGNAL_TABS as readonly string[]).includes(v)

/** The pane's Knowledge base section (signals/get_kb). */
export interface SignalKbView {
  text: string
  loading: boolean
  error: string | null
}

/** The raw-event task behind the Overview breakdown (lib/signal-evidence). */
export interface SignalEvidenceView {
  loading: boolean
  error: string | null
  /** The signal has no raw-event query. */
  noQuery: boolean
  facets: SignalFacet[]
  onRetry: () => void
}

/** One page of the task's raw events — the Raw events section. */
export interface SignalEventsView {
  loading: boolean
  error: string | null
  rows: Record<string, unknown>[]
  fields: string[]
  total: number | null
  pageNo: number
  pageSize: number
  onPage: (pageNo: number) => void
}

export interface SignalPanelProps {
  signal: Signal
  triage: SignalTriage | null
  /** Every signal in the window — storyline + prior-handling derive from it. */
  all: Signal[]
  /** The storyline (connected-signals subgraph) the page resolved for this signal — the one it was opened from, else the cached one holding it; null → same-entity fallback. */
  storyline: PromotedSubgraph | null
  /** True when the signal is not in the loaded rows of the current queue tab (opened from a storyline); the header says so. */
  outsideTab?: boolean
  /** The signal's tags resolved against signals/tag/list (unknown / disabled dropped); none = no chips. */
  tags?: readonly SignalTag[]
  /** Docked pane only: the storyline the signal was opened from — a `← storyline` link returns the pane to it. */
  backToStoryline?: { title: string; onBack: () => void }
  /** Triage records for the whole window (prior handling, member status). */
  triageMap: Map<string, SignalTriage>
  /** Whether the inline deep panel (tier 1) is expanded. */
  deepOpen: boolean
  /**
   * The rendered `<DeepPanel/>` element — the page owns cluster/scope and the
   * pin/note writes, so it builds the element and the panel only places it
   * (under Raw events) while `deepOpen`.
   */
  deep?: ReactNode
  /**
   * Suppression rules whose terms match this signal's entities — the page
   * derives them from useSuppressionRules (the panel has no cluster/scope).
   */
  suppressions?: { id: string; summary: string }[]
  /**
   * The 30d baseline (lib/signal-history useSignalBaseline). When present,
   * prior handling and the per-entity "N other signals" counts read from it
   * instead of the window.
   */
  baseline?: Signal[]
  /** Inline 30d context per entity chip (entityKey → count/first/rare). */
  entityContext?: Record<string, EntityChipContext>
  /**
   * `sheet` (default): the full panel in a SlideOver. `docked`: the compact
   * pane beside the queue — header, reason, AI card, entities, storyline,
   * raw events (+ the deep panel), footer; ⤢ (`onPromote`) opens the sheet.
   */
  variant?: 'sheet' | 'docked'
  /** Demo-only AI triage suggestion (lib/signal-ai); absent = the card is not rendered. */
  suggestion?: TriageSuggestion | null
  /** Accept opens the Close dialog prefilled — it never applies by itself. */
  onAcceptSuggestion?: () => void
  onSuggestionNotHelpful?: () => void
  /** Open the source workbook after the lock check; absent = no button. */
  onOpenWorkbook?: () => void
  /** The lock check is running. */
  workbookBusy?: boolean
  /** Why Open workbook refused (lib/signal-queue lockRefusal); null = nothing to show. */
  workbookRefusal?: string | null
  /** Docked only: promote to the full sheet. */
  onPromote?: () => void
  /** Docked only: the section the pane scrolls to on open (`?ptab`), and the index click that sets it (the page also expands it). */
  tab?: SignalTab
  onTab?: (tab: SignalTab) => void
  /** Docked only: the expanded sections — the page owns them, since each section's read runs only while it is expanded. */
  expanded?: ReadonlySet<SignalTab>
  onToggleSection?: (tab: SignalTab) => void
  /** Docked only: the sections' data — absent renders the section's empty state. */
  kb?: SignalKbView
  evidence?: SignalEvidenceView
  events?: SignalEventsView
  /** Docked only: mark / unmark the signal false positive on the server (the legacy drawer's toggle). */
  onToggleFalsePositive?: () => void
  falsePositiveBusy?: boolean

  onClose: () => void
  /** The chip's history button — the page opens the entity flyout. */
  onOpenEntity: (entity: SignalEntity) => void
  onToggleDeep: () => void
  onOpenAsHunt: () => void
  onEscalate: () => void
  onCloseSignal: () => void
  onSuppress: () => void
  onReopen: () => void
  onAddNote: (text: string) => void
  onUnpin: (pinId: string) => void
  /** Pivot: open Fabric search with this query (IA6 contract). */
  onOpenSearch: (query: string) => void
  onOpenDetection: () => void
  /** Click on a storyline member. */
  onSelectSignal: (uuid: string) => void
}

export interface CloseDialogProps {
  open: boolean
  /** The signals being closed — one or a bulk selection. */
  signals: Signal[]
  /** Pre-selected disposition (the `s` suppress shortcut opens with FP – rule logic). */
  preset?: Disposition | null
  onCancel: () => void
  onConfirm: (input: {
    uuids: string[]
    disposition: Disposition
    dispositionType: string | null
    note: string
    /** Mirror to the legacy FP flag (/signals/add_rule). */
    flagFp: boolean
    /** Create this suppression rule (FP – rule logic only). */
    suppression: { wbId: string; wbName: string; terms: SuppressionTerms } | null
  }) => void
}

/** The Existing case picker's page — V2 cases, status / category filters only (no search on the service). */
export interface EscalateCasesView {
  items: CmCase[]
  total: number
  page: number
  pageSize: number
  loading: boolean
  error: string | null
  statuses: CmStatus[]
  category: CmCategory | null
  onCategory: (c: CmCategory | null) => void
  status: string | null
  onStatus: (key: string | null) => void
  onPage: (page: number) => void
}

export interface EscalateDialogProps {
  open: boolean
  signals: Signal[]
  cases: EscalateCasesView
  /** Assignee picker names for a new case. */
  users: string[]
  /** From the hook: set after a create whose attach failed, so retry only attaches. */
  pending: { caseId: string; caseNumber: string | null; error: string } | null
  busy: boolean
  /** A failed create, said inline. */
  error?: string | null
  /**
   * A whole storyline: its own title and summary, the member count (more
   * than the loaded `signals` when some are not in the window) and the mode
   * to open in (Add to case… opens on an existing case).
   */
  prefill?: { title?: string; description?: string; severity?: string; count?: number; mode?: 'new' | 'existing' }
  onCancel: () => void
  onConfirm: (input: { caseId?: string; caseNumber?: string | null; title?: string; description?: string; severity?: CmSeverity; assignee?: string }) => void
}

/**
 * The storyline drawer — the docked pane for one connected-signals subgraph
 * (docs/signals.md §Storyline panel). Props in, events out: the page owns the
 * URL (`?sg`, `?stab`), the queries and the case flow.
 */
export interface StorylinePanelProps {
  storyline: PromotedSubgraph
  tab: StoryTab
  onTab: (tab: StoryTab) => void
  /** A member's full row when one is loaded (window → 30d baseline); null renders the subgraph's own facts. */
  resolveSignal: (uuid: string) => Signal | null
  openUuid: string | null
  /** models/signals_attribution for this storyline. */
  campaigns: Campaign[] | undefined
  campaignsLoading: boolean
  campaignsError: string | null
  onRetryCampaigns: () => void
  /** ATT&CK tactic order for the MITRE tab (attack/matrix). */
  tacticOrder: readonly string[]
  /** Watched entity keys (signal-model entityKey). */
  watched: ReadonlySet<string>
  onOpenSignal: (uuid: string) => void
  onOpenEntity: (entity: SignalEntity) => void
  onOpenSearch: (query: string) => void
  onToggleWatch: (entityKey: string) => void
  /** Add to case: the dialog offers a new case or an existing one. */
  onAddToCase: () => void
  onClose: () => void
}

/** Add a remark to one signal (signals/add_signal_remark). */
export interface RemarkDialogProps {
  open: boolean
  signal: Signal | null
  busy: boolean
  /** The last save's failure, shown inline. */
  error: string | null
  onCancel: () => void
  onConfirm: (remark: string) => void
}

/**
 * The entity flyout (decision S15) — a second SlideOver stacked over the
 * signal panel. Everything is derived by the page over the 30d baseline;
 * the flyout renders and calls back.
 */
export interface EntityFlyoutProps {
  entity: SignalEntity
  history: EntityHistory
  prevalence: Prevalence
  /** Cases holding a signal with this entity (from triage caseId over the baseline); `number` is `case-N` or `—`. */
  cases: { id: string; number: string; title: string }[]
  suppressions: MatchingRule[]
  watched: boolean
  open: boolean
  /** Last 8 signals carrying this entity, newest first. */
  recent: Signal[]
  onClose: () => void
  onToggleWatch: () => void
  onOpenSearch: (query: string) => void
  onOpenAsHuntFromEntity?: () => void
  onSelectSignal: (uuid: string) => void
}

export interface DeepPanelProps {
  clusterId: string
  scopeId: string
  signal: Signal
  triage: SignalTriage | null
  /** The storyline's members (else same-entity signals) + this signal — the mini graph and timeline overlay. */
  related: Signal[]
  user: string
  onPin: (pin: Omit<PinnedRow, 'id' | 'at'>) => void
  onUnpin: (pinId: string) => void
  onAddNote: (text: string) => void
  onOpenSearch: (query: string) => void
  onOpenAsHunt: () => void
  onSelectSignal: (uuid: string) => void
}
